import { TypographyH2, TypographyH3 } from '@heidi/design-system/typography';
import { BreathingLogoMotifAnimation } from '@heidi/snowflake-ui/breathing-logo-motif-animation';
import { finishTracking, TrackingKey } from '@heidi/runtime/performance';
import { useQueryUser } from '@heidi/workspace/data';
import { cn } from '@/lib/cn';
import { getUserGreetingName } from '@/utils/i18n/cjkLocales';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  CHAT_EMPTY_STATE_GREETING_KEYS,
  chatEmptyStateMessages,
} from './ChatEmptyState.messages';
import { EvidenceVerbSuggestions } from './evidence-verb-suggestions';

interface ChatEmptyStateProps {
  isSessionless?: boolean;
  /** Called when a verb suggestion is clicked — populates the input (in-session only) */
  onPopulateInput?: (starter: string) => void;
  /** Called when the Fill verb chip is clicked — opens the template picker */
  onFillVerbClick?: () => void;
  pageOpenTrackingRoute?: 'scribe_chat' | 'chat' | 'scribe_session';
}

export const ChatEmptyState = ({
  isSessionless = false,
  onPopulateInput,
  onFillVerbClick,
  pageOpenTrackingRoute,
}: ChatEmptyStateProps) => {
  const intl = useIntl();
  const { data: userNames } = useQueryUser((data) => ({
    first_name: data?.first_name ?? null,
    last_name: data?.last_name ?? null,
  }));

  const greetingName = useMemo(
    () => getUserGreetingName(userNames, intl.locale),
    [userNames, intl.locale]
  );

  const greetingMessageKey = useMemo(() => {
    const keys = CHAT_EMPTY_STATE_GREETING_KEYS;
    const index = Math.floor(Math.random() * keys.length);
    return keys[index] ?? keys[0];
  }, []);

  const greetingValues = useMemo(
    () => ({
      hasName: greetingName ? 'true' : 'other',
      name: greetingName ?? '',
    }),
    [greetingName]
  );

  const greeting = (
    <FormattedMessage
      {...chatEmptyStateMessages[greetingMessageKey]}
      values={greetingValues}
    />
  );

  // In-session state: tracks whether the verb popover is expanded (to fade the greeting).
  // Declared before early return to satisfy Rules of Hooks.
  const [isVerbsExpanded, setIsVerbsExpanded] = useState(false);
  const handleExpandedChange = useCallback((isExpanded: boolean) => {
    setIsVerbsExpanded(isExpanded);
  }, []);

  useEffect(() => {
    if (!pageOpenTrackingRoute) {
      return;
    }

    const raf = requestAnimationFrame(() => {
      finishTracking({
        key: TrackingKey.EVIDENCE_NEW_CHAT,
        metadata: {
          route: pageOpenTrackingRoute,
          chat_id: null,
          ready_state: 'overview',
        },
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [pageOpenTrackingRoute]);

  if (isSessionless) {
    return (
      <div className="flex flex-col items-start gap-4 text-left sm:items-center sm:text-center">
        <BreathingLogoMotifAnimation size={40} variant="yellow" />
        <TypographyH2 className="text-text-primary">{greeting}</TypographyH2>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col p-6">
      {/* Top spacer — absorbs equal space above the greeting */}
      <div className="min-h-0 flex-1" />

      {/* Greeting — sits at the true vertical center between the two spacers */}
      <div
        className={cn(
          'transition-opacity duration-200 ease-in-out',
          isVerbsExpanded && 'pointer-events-none opacity-0'
        )}
      >
        <div className="flex flex-col items-start gap-4">
          <BreathingLogoMotifAnimation size={40} variant="yellow" />
          <TypographyH3 className="text-text-primary">{greeting}</TypographyH3>
        </div>
      </div>

      {/* Bottom spacer — absorbs equal space below the greeting */}
      <div className="min-h-0 flex-1" />

      {/* Verb suggestions — rendered after the spacer so the popover anchors to the true bottom */}
      {onPopulateInput && (
        <div className="w-full pt-6">
          <EvidenceVerbSuggestions
            onPopulateInput={onPopulateInput}
            onExpandedChange={handleExpandedChange}
            onFillVerbClick={onFillVerbClick}
            isMobile
          />
        </div>
      )}
    </div>
  );
};
